import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';

import { AppStorage } from 'app/app.storage';
import { LocalizationStore } from '@core/localization';
import { NavigationService } from '@core/navigation';
import { UxService } from '@core/ux';
import { SplashService } from '@shared/components/splash/splash.service';

import { LayoutStore } from './layout.store';

// Single owner of the app's router-event side effects, extracted from the former
// AppComponent.navigationInterceptor god method:
//   - navigation loading class on `.content`
//   - header/footer visibility + programme flag (via LayoutStore)
//   - language-from-URL handling
//   - details / breadcrumb teardown
//   - one-shot boot-splash teardown on the first settled navigation
//
// Instantiating it starts the subscription, so AppComponent injects it BEFORE the
// boot redirect runs — the very first NavigationEnd must be observed. DOM access
// for `.content` / `.autologin.overlay` is intentionally kept here (behaviour
// preserved); a later pass can move it to MainLayoutComponent bindings.
@Injectable({ providedIn: 'root' })
export class NavigationUiService {
    private readonly router = inject(Router);
    private readonly navigation = inject(NavigationService);
    private readonly layout = inject(LayoutStore);
    private readonly storage = inject(AppStorage);
    private readonly localizations = inject(LocalizationStore);
    private readonly ux = inject(UxService);
    private readonly splash = inject(SplashService);
    private readonly destroyRef = inject(DestroyRef);

    private splashTornDown = false;

    constructor() {
        // The initial navigation can settle before this service is constructed
        // (a synchronous resolver straight to /account/login); tear the splash
        // down immediately in that case, otherwise on the first settle below.
        if (this.router.navigated) {
            this.tearDownSplash();
        }

        this.router.events
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(event => this.handle(event));
    }

    private handle(event: unknown): void {
        if (event instanceof NavigationStart) {
            this.onStart(event);
        } else if (event instanceof NavigationEnd) {
            this.onEnd(event);
        } else if (event instanceof NavigationError) {
        }

        if (event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError) {
            this.tearDownSplash();
        }
    }

    private onStart(event: NavigationStart): void {
        this.navigationStarted(event.url);
        this.toggleContentLoading(event.url, true);
        this.layout.setProgramme(event.url.indexOf('/guide') >= 0);
        this.applyLanguageFromUrl(event.url);
    }

    // Update UX route state + navigation context on every navigation start. Folded in
    // from the retired app-wide SharedService (this service was its only caller).
    private navigationStarted(url: string): void {
        this.ux.routeUrl = url;
        if (this.ux.searching) {
            this.ux.closeSearch();
        }
        if (this.navigation.context.restored) {
            this.navigation.context.restored = false;
        } else if (url && url.indexOf('/consent') < 0) {
            this.navigation.context.route = url;
        }
    }

    private onEnd(event: NavigationEnd): void {
        // Details teardown on navigation is owned by DetailsShell (its own NavigationEnd +
        // onUrlChange listeners call store.close() when the URL drops the `#details` hash).
        if (event.url.indexOf('/account') < 0) {
            this.toggleContentLoading(event.url, false);
        } else {
            // Re-assert the active language on /account pages (no-op once ready).
            this.localizations.setLanguage(this.storage.languageId)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe();
        }
        this.ux.setAdditionalHeaderStyles();
        this.hideAutologinOverlay(event.url);
        // Resolve chrome visibility from the POST-redirect URL: the anonymous boot
        // funnel navigates to `/login?returnUrl=…`, which the router redirects to
        // `/account/login`. `event.url` is the pre-redirect `/login?…` (which the
        // resolver treats as header-visible), so using it leaks the global header
        // onto the account page — `urlAfterRedirects` is the route actually rendered.
        this.layout.applyForUrl(event.urlAfterRedirects);
    }

    // Toggle the navigation loading state on the routed content area. Skipped on
    // /account/* (which renders its own chrome and never shows this spinner).
    private toggleContentLoading(url: string, loading: boolean): void {
        if (url.indexOf('/account') >= 0) {
            return;
        }
        const content = document.querySelector('.content') as HTMLElement | null;
        content?.classList.toggle('loading', loading);
    }

    private hideAutologinOverlay(url: string): void {
        if (url.indexOf('/login') >= 0 || url === '/') {
            return;
        }
        const overlay = document.querySelector('.autologin.overlay') as HTMLElement | null;
        if (overlay && overlay.offsetParent !== null) {
            overlay.style.display = 'none';
        }
    }

    // Honour a `languageId` carried directly on the URL or inside its returnUrl,
    // switching the active language when it matches a known one.
    private applyLanguageFromUrl(url: string): void {
        let languageId: string | null = null;
        const direct = url.match(/[?&]languageId=([^&]*)/);
        if (direct) {
            languageId = direct[1];
        }
        if (!languageId) {
            const returnUrlMatch = url.match(/[?&]returnUrl=([^&]*)/);
            if (returnUrlMatch) {
                const decoded = decodeURIComponent(returnUrlMatch[1]);
                const inReturn = decoded.match(/[?&]languageId=([^&]*)/);
                if (inReturn) {
                    languageId = inReturn[1];
                }
            }
        }
        if (!languageId || !(this.storage.languages?.length > 0)) {
            return;
        }
        const language = this.storage.languages.find((l: any) => l.referenceId === languageId);
        if (!language) {
            return;
        }
        this.storage.languageCode = language.code;
        this.storage.languageId = languageId;
        this.localizations.setLanguage(languageId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({ error: err => console.error('Failed to change language:', err) });
    }

    // Boot splash is reserved for the initial render; hide it on the first settled
    // navigation (success, cancel, or error) and never touch it again.
    private tearDownSplash(): void {
        if (this.splashTornDown) {
            return;
        }
        this.splashTornDown = true;
        this.splash.hide();
    }
}
